import { useState } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  AlertCircleIcon,
  ArrowDown01Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant } from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplyCustomButtonLinkResultDialog } from "shared/app/ApplyCustomButtonLink/ApplyCustomButtonLinkResultDialog";
import {
  targetLabel,
  type PropertyDictionary
} from "shared/app/ApplyCustomButtonLink/helpers";
import {
  ApplyCustomButtonLinkResponse,
  CustomButtonLinkScope
} from "types/landlord/address";
import {
  isSettledStatus,
  useBulkBrandingProgress
} from "providers/BulkBrandingProgress";
import { useBulkCustomButtonLinkProgress } from ".";

type Tone = "pending" | "success" | "warning" | "error" | "cancelled";

const TONE_ICON: Record<Tone, IconSvgElement | null> = {
  pending: null,
  success: Tick02Icon,
  warning: AlertCircleIcon,
  error: AlertCircleIcon,
  cancelled: AlertCircleIcon
};

const TONE_COLOR_CLASS: Record<Tone, string> = {
  pending: "",
  success: "text-success-500",
  warning: "text-warning-500",
  error: "text-danger-500",
  cancelled: "text-content-400"
};

const getTone = ({
  isStatusError,
  hasGivenUpPolling,
  status,
  hasIssues,
  isSettled
}: {
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  status: ApplyCustomButtonLinkResponse | undefined;
  hasIssues: boolean;
  isSettled: boolean;
}): Tone => {
  if (isStatusError || status?.status === "failed") return "error";
  if (status?.status === "cancelled") return "cancelled";
  if (isSettled && hasIssues) return "warning";
  if (hasGivenUpPolling) return "warning";
  if (status?.status === "completed") return "success";
  return "pending";
};

const getTitle = ({
  tone,
  isStatusError,
  hasGivenUpPolling
}: {
  tone: Tone;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
}) => {
  if (tone === "error" && isStatusError) return "Couldn't check progress";
  if (tone === "error") return "Could not apply custom button link";
  if (tone === "cancelled") return "Custom button link cancelled";
  if (tone === "warning" && hasGivenUpPolling)
    return "Taking longer than expected";
  if (tone === "warning") return "Link applied with some errors";
  if (tone === "success") return "Custom button link applied";
  return "Applying custom button link...";
};

const getDescription = ({
  tone,
  isStatusError,
  hasGivenUpPolling,
  status,
  scope,
  dictionary
}: {
  tone: Tone;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  status: ApplyCustomButtonLinkResponse | undefined;
  scope: CustomButtonLinkScope;
  dictionary: PropertyDictionary;
}) => {
  if (isStatusError)
    return "The link is still applying in the background — reload the page to see the result.";
  if (tone === "error")
    return (
      status?.error ||
      "Something went wrong while applying the link. Please try again."
    );
  if (hasGivenUpPolling)
    return "This is taking longer than expected. It's likely still running in the background — reload the page to check on it.";
  if (!status) return "This may take a moment.";
  if (tone === "cancelled") {
    const pct = status.total_count
      ? Math.round((status.processed_count / status.total_count) * 100)
      : null;
    const stoppedAt = pct !== null ? ` at ${pct}%` : "";
    const keptLabel = targetLabel(status.updated_count, scope, dictionary);
    return `Stopped${stoppedAt} — ${status.updated_count} ${keptLabel} already updated were kept.`;
  }
  if (tone === "warning" || tone === "success")
    return `Applied to ${status.updated_count} ${targetLabel(status.updated_count, scope, dictionary)}.`;
  if (status.total_count)
    return `${status.processed_count} of ${status.total_count} ${targetLabel(status.total_count, scope, dictionary)}`;

  return "This may take a moment.";
};

const getProgressPercent = (
  status: ApplyCustomButtonLinkResponse | undefined,
  isSettled: boolean
) => {
  if (!status?.total_count || isSettled) return null;
  return Math.min(
    100,
    Math.round((status.processed_count / status.total_count) * 100)
  );
};

const getCountText = (
  status: ApplyCustomButtonLinkResponse | undefined,
  scope: CustomButtonLinkScope,
  dictionary: PropertyDictionary
) => {
  if (status?.total_count == null) return null;
  return `${status.processed_count} / ${status.total_count} ${targetLabel(status.total_count, scope, dictionary)}`;
};

const getPrimaryLabel = ({
  isSettled,
  isStatusError,
  hasGivenUpPolling,
  hasRequestedCancel,
  canCancel
}: {
  isSettled: boolean;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  hasRequestedCancel: boolean;
  canCancel: boolean;
}) => {
  if (isSettled || isStatusError || hasGivenUpPolling) return "Dismiss";
  if (hasRequestedCancel) return "Cancelling…";
  if (canCancel) return "Cancel";
  return null;
};

const PanelIcon = ({
  tone,
  icon
}: {
  tone: Tone;
  icon: IconSvgElement | null;
}) => {
  if (!icon)
    return (
      <div className="size-4 shrink-0 animate-spin rounded-full border-2 border-alpha-200 border-t-primary-500" />
    );

  return (
    <HugeiconsIcon
      className={`shrink-0 ${TONE_COLOR_CLASS[tone]}`}
      icon={icon}
      size={20}
    />
  );
};

const PanelBody = ({
  description,
  progressPercent,
  countText,
  showViewDetails,
  primaryLabel,
  primaryAction,
  isPrimaryActionBusy,
  onViewDetails
}: {
  description: string;
  progressPercent: number | null;
  countText: string | null;
  showViewDetails: boolean;
  primaryLabel: string | null;
  primaryAction: () => void;
  isPrimaryActionBusy: boolean;
  onViewDetails: () => void;
}) => (
  <div className="space-y-2 px-4 pb-4">
    <p className="text-sm text-content-400">{description}</p>
    {progressPercent !== null ? (
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-alpha-80">
        <div
          className="h-full rounded-full bg-primary-500 transition-all duration-200"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    ) : null}

    <div className="flex items-center justify-between gap-2 pt-1">
      <p className="text-xs text-content-300">{countText}</p>
      <div className="flex shrink-0 items-center gap-1">
        {showViewDetails ? (
          <Button onClick={onViewDetails} variant={ButtonVariant.TEXT}>
            View details
          </Button>
        ) : null}
        {primaryLabel ? (
          <Button
            disabled={isPrimaryActionBusy}
            loading={isPrimaryActionBusy}
            onClick={primaryAction}
            variant={ButtonVariant.TEXT}
          >
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  </div>
);

export const BulkCustomButtonLinkProgressPanel = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const {
    run,
    status,
    isStatusError,
    hasGivenUpPolling,
    isCancelling,
    hasRequestedCancel,
    cancelRun,
    dismiss
  } = useBulkCustomButtonLinkProgress();
  const { run: brandingRun } = useBulkBrandingProgress();
  const [expanded, setExpanded] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  if (run === null) return null;

  const isSettled = Boolean(status && isSettledStatus(status.status));
  const hasIssues = Boolean(status && status.failed.length > 0);

  const tone = getTone({
    isStatusError,
    hasGivenUpPolling,
    status,
    hasIssues,
    isSettled
  });
  const title = getTitle({ tone, isStatusError, hasGivenUpPolling });
  const description = getDescription({
    tone,
    isStatusError,
    hasGivenUpPolling,
    status,
    scope: run.scope,
    dictionary
  });
  const icon = TONE_ICON[tone];

  const progressPercent = getProgressPercent(status, isSettled);
  const countText = getCountText(status, run.scope, dictionary);
  const canCancel = !isSettled && Boolean(status?.cancellable);
  const primaryLabel = getPrimaryLabel({
    isSettled,
    isStatusError,
    hasGivenUpPolling,
    hasRequestedCancel,
    canCancel
  });
  const primaryAction =
    isSettled || isStatusError || hasGivenUpPolling ? dismiss : cancelRun;
  const isPrimaryActionBusy =
    !isSettled && (isCancelling || hasRequestedCancel);

  return (
    <>
      <div
        className={`fixed right-4 z-[9999] w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-alpha-40 bg-background-1000 shadow-xl ${brandingRun !== null ? "top-28" : "top-4"}`}
      >
        <button
          className="flex w-full items-center justify-between gap-2 p-4 text-left"
          onClick={() => setExpanded((prev) => !prev)}
          type="button"
        >
          <div className="flex min-w-0 items-center gap-2">
            <PanelIcon icon={icon} tone={tone} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-content-1000">{title}</p>
              {!expanded ? (
                <p className="truncate text-xs text-content-400">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          <HugeiconsIcon
            className={`shrink-0 text-content-300 transition-transform ${expanded ? "rotate-180" : ""}`}
            icon={ArrowDown01Icon}
            size={16}
          />
        </button>

        {expanded ? (
          <PanelBody
            countText={countText}
            description={description}
            isPrimaryActionBusy={isPrimaryActionBusy}
            onViewDetails={() => setShowDetails(true)}
            primaryAction={primaryAction}
            primaryLabel={primaryLabel}
            progressPercent={progressPercent}
            showViewDetails={isSettled && hasIssues}
          />
        ) : null}
      </div>

      <ApplyCustomButtonLinkResultDialog
        onClose={() => setShowDetails(false)}
        result={showDetails ? (status ?? null) : null}
        scope={run.scope}
      />
    </>
  );
};
